import { fetchWithTimeout } from "./httpClient";
import { parseJsonSafely } from "./jsonParser";
import { throwBackendError } from "./errorMapper";

interface ApiRequestOptions extends RequestInit {
  timeout?: number;
}

interface BackendSuccessResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  meta?: Record<string, unknown>;
}

interface BackendErrorPayload {
  type?: string;
  message?: string;
  requestId?: string;
}

export interface BackendErrorResponse {
  error?: string | BackendErrorPayload;
  message?: string;
}

export function normalizeBackendError(errorData: BackendErrorResponse): { errorType: string; message: string } {
  const payload = errorData.error;
  const errorType = typeof payload === "string" ? payload : (payload?.type ?? "SystemError");
  const requestId = typeof payload === "object" ? payload?.requestId : undefined;
  const message = (typeof payload === "object" && payload?.message) || errorData.message || "Request failed";
  const normalizedMessage = requestId ? `${message} (Ref: ${requestId})` : message;
  return { errorType, message: normalizedMessage };
}

export async function apiRequest<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await fetchWithTimeout(url, options);

  if (!response.ok) {
    const errorData = await parseJsonSafely<BackendErrorResponse>(response);
    const { errorType, message } = normalizeBackendError(errorData);
    throwBackendError(errorType, message);
  }

  // Handle 204 No Content (common for DELETE operations)
  if (response.status === 204) {
    return {} as T;
  }

  return await parseJsonSafely<T>(response);
}

/**
 * Generic API GET request
 */
export async function apiGet<T>(url: string, options?: ApiRequestOptions): Promise<T> {
  return apiRequest<T>(url, { ...options, method: "GET" });
}

/**
 * Generic API GET request with data wrapper extraction
 * Automatically extracts data from { success, data } response structure
 */
export async function apiGetWithData<T>(url: string, options?: ApiRequestOptions): Promise<T> {
  return apiRequestWithData<T>(url, {
    ...options,
    method: "GET",
  });
}

/**
 * Read a non-OK response that may carry either a JSON error envelope
 * or an empty/plain-text body (typical for streaming endpoints like CSV exports).
 * Parsing is isolated so a JSON.parse failure can never swallow a legitimate
 * backend error type (NotFoundError, ValidationError, ...).
 */
async function parseTextResponseError(response: Response): Promise<{ errorType: string; message: string }> {
  const text = await response.text();
  const statusBit = `${response.status}${response.statusText ? ` ${response.statusText}` : ""}`.trim();
  if (!text.trim()) {
    return { errorType: "SystemError", message: `Request failed (${statusBit})` };
  }
  let parsed: BackendErrorResponse;
  try {
    parsed = JSON.parse(text) as BackendErrorResponse;
  } catch {
    return { errorType: "SystemError", message: `Request failed (${statusBit})` };
  }
  if (!parsed || typeof parsed !== "object") {
    return { errorType: "SystemError", message: `Request failed (${statusBit})` };
  }
  return normalizeBackendError(parsed);
}

/**
 * GET request that returns the raw response body as text.
 * Use for endpoints that stream non-JSON payloads where plain text is acceptable.
 * For UTF-8 CSV streams that must be opened in Excel, prefer `apiGetBlob`
 * because `response.text()` can drop the BOM during decode.
 */
export async function apiGetText(url: string, options?: ApiRequestOptions): Promise<string> {
  const response = await fetchWithTimeout(url, { ...options, method: "GET" });
  if (!response.ok) {
    const { errorType, message } = await parseTextResponseError(response);
    throwBackendError(errorType, message);
  }
  return response.text();
}

/**
 * GET request that returns the raw response body as a Blob.
 * Preserves byte-level fidelity — required for UTF-8 BOM survival in CSV
 * downloads that Excel must auto-detect as UTF-8.
 */
export async function apiGetBlob(url: string, options?: ApiRequestOptions): Promise<Blob> {
  const response = await fetchWithTimeout(url, { ...options, method: "GET" });
  if (!response.ok) {
    const { errorType, message } = await parseTextResponseError(response);
    throwBackendError(errorType, message);
  }
  return response.blob();
}

/**
 * Generic API POST request
 */
export async function apiPost<T>(url: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  return apiRequest<T>(url, {
    ...options,
    method: "POST",
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Generic API PATCH request
 */
export async function apiPatch<T>(url: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  return apiRequest<T>(url, {
    ...options,
    method: "PATCH",
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Generic API DELETE request
 */
export async function apiDelete<T>(url: string, options?: ApiRequestOptions): Promise<T> {
  return apiRequest<T>(url, { ...options, method: "DELETE" });
}

export async function apiDeleteWithData<T>(url: string, options?: ApiRequestOptions): Promise<T> {
  return apiRequestWithData<T>(url, { ...options, method: "DELETE" });
}

/**
 * Handle backend response with data wrapper
 * Many endpoints return { success, data } structure
 * Handles 204 No Content responses
 */
export async function apiRequestWithData<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await fetchWithTimeout(url, options);

  if (!response.ok) {
    const errorData = await parseJsonSafely<BackendErrorResponse>(response);
    const { errorType, message } = normalizeBackendError(errorData);
    throwBackendError(errorType, message);
  }

  // Handle 204 No Content (common for DELETE operations)
  if (response.status === 204) {
    return {} as T;
  }

  const result = await parseJsonSafely<BackendSuccessResponse<T>>(response);
  return result.data;
}

/**
 * Generic API PATCH request with data wrapper extraction
 * Automatically extracts data from { success, data } response structure
 */
export async function apiPatchWithData<T>(url: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  return apiRequestWithData<T>(url, {
    ...options,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Generic API POST request with data wrapper extraction
 * Automatically extracts data from { success, data } response structure
 */
export async function apiPostWithData<T>(url: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  return apiRequestWithData<T>(url, {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}
